import { mergeWith } from 'lodash-es'

import { formatNotificationToggleValues, NOTIFICATION_TOGGLE_KINDS } from './notification_toggles'
import { formValuesToServerData } from '../../notifications/subsections/config'

// Replace arrays (e.g. `timings`) wholesale instead of lodash's default
// index-wise array merge, so an edited draft fully overrides the toggle's
// reconstructed values rather than blending element-by-element.
const replaceArrays = (_destination, source) => (Array.isArray(source) ? source : undefined)

const deepMergeAttributes = (...attributesByForm) => mergeWith({}, ...attributesByForm, replaceArrays)

// Content-edit persistence for the CRUD notifications drawer. When a user edits a
// notification's subject/body/timings, the drawer stages the form-shaped values
// into the parent root form under `notificationDrafts[kind]` instead of issuing a
// PATCH. At save time we convert each draft to server data with the existing
// `formValuesToServerData` (so template-variable handling is unchanged) and keep
// only the message content — the shared settings (reply-to, cancellation) live on
// the landing view, and the enabled flag is owned by the activation toggle.

export const getNotificationDraftsInitialValues = () => ({ notificationDrafts: {} })

const stripEnabled = ({ enabled: _enabled, ...rest }) => rest

const formatDraft = (kind, draft) => {
  const serverData = formValuesToServerData(draft, kind)
  const toggleOwnsEnabled = NOTIFICATION_TOGGLE_KINDS.includes(kind)
  const content = {}

  if (serverData.message_customizations) {
    content.message_customizations = Object.fromEntries(
      Object.entries(serverData.message_customizations).map(([customizationKind, value]) => [
        customizationKind,
        toggleOwnsEnabled ? stripEnabled(value) : value
      ])
    )
  }

  const attributesKey = `${kind}_attributes`
  if (serverData[attributesKey]) {
    content[attributesKey] = toggleOwnsEnabled
      ? stripEnabled(serverData[attributesKey])
      : serverData[attributesKey]
  }

  return content
}

const formatDrafts = (drafts = {}) => (
  Object.entries(drafts).reduce((attributes, [kind, draft]) => (
    draft ? deepMergeAttributes(attributes, formatDraft(kind, draft)) : attributes
  ), {})
)

// Merges activation-toggle changes with staged content drafts. Drafts are merged
// last so an explicit content edit (subject/body/timings) wins over the toggle's
// reconstructed values, while `enabled` stays owned by the toggle.
export const formatCrudNotificationContent = ({ eventType, values, initialValues }) => deepMergeAttributes(
  formatNotificationToggleValues({ eventType, values, initialValues }),
  formatDrafts(values?.notificationDrafts)
)
